import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { riskApi } from '../../utils/riskApi';
import { formatStamp } from '../../utils/datetime';
import '../../styles/DocReview.css';
import '../../styles/Risk.css';

/**
 * Risk reports (design §11.3): one list on the left, the report on the right. Every report reads
 * the panel snapshot and names the scoring it used; "Download Excel" writes all of them.
 */
export default function ReportsPage() {
  const { setupId } = useParams();
  const [panel, setPanel] = useState(null);
  const [list, setList] = useState([]);
  const [kind, setKind] = useState('summary');
  const [rep, setRep] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    riskApi.panelConfig(setupId).then(setPanel).catch((e) => setError(e.message));
    riskApi.call('GET', `/api/risk/panels/${setupId}/reports`).then((d) => setList(d.reports || [])).catch((e) => setError(e.message));
  }, [setupId]);

  useEffect(() => {
    let live = true;
    setRep(null);
    riskApi.call('GET', `/api/risk/panels/${setupId}/reports/${kind}`)
      .then((d) => { if (live) setRep(d); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [setupId, kind]);

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(`/api/risk/panels/${setupId}/reports.xlsx`, { credentials: 'include' });
      if (!res.ok) throw new Error(`Download failed (${res.status})`);
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `${panel?.name || 'risk'} - risk reports.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const r = rep?.report;
  return (
    <div className="drv-page rsk-page">
      <div className="rsk-crumbs">
        <Link to="/risk">Risk</Link><span>/</span>
        <span className="rsk-dim">{panel?.name || 'Risk file'}</span><span>/</span><strong>Reports</strong>
      </div>
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Reports</h1>
          <p className="drv-sub">Read from the risk file{rep?.built_at ? ` at ${formatStamp(rep.built_at)}` : ''} · scoring: {rep?.settings?.source === 'default' ? 'default table' : 'Orcanos FMEA settings'}</p>
        </div>
        <div className="rsk-actions">
          <button type="button" className="drv-btn drv-btn--lg" onClick={() => window.print()}>Print</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={download} disabled={busy}>
            {busy ? 'Preparing…' : 'Download Excel (all reports)'}
          </button>
        </div>
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}
      <div className="rsk-two">
        <nav className="rsk-replist" aria-label="Reports">
          {list.map((x) => (
            <button key={x.kind} type="button" className={`rsk-repbtn${kind === x.kind ? ' on' : ''}`} onClick={() => setKind(x.kind)}>{x.title}</button>
          ))}
        </nav>
        <section className="rsk-card rsk-pad rsk-repbody" aria-live="polite">
          {!r && <div className="drv-muted">Building the report…</div>}
          {r && (
            <>
              <h2 className="rsk-card-title">{r.title}</h2>
              <p className="rsk-sub" style={{ maxWidth: 'none' }}>{r.intro}</p>
              <div className="rsk-table-wrap">
                <table className="rsk-table" style={{ minWidth: r.columns.length > 4 ? 960 : 520 }}>
                  <thead><tr>{r.columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
                  <tbody>
                    {r.rows.map((row, i) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <tr key={i}>{row.map((v, j) => <td key={j} className={j > 1 ? 'rsk-sub' : undefined}>{String(v ?? '')}</td>)}</tr>
                    ))}
                    {r.rows.length === 0 && (
                      <tr><td colSpan={r.columns.length} className="rsk-dim" style={{ textAlign: 'center', padding: 20 }}>Nothing to report.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
